import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Star, CheckCircle, Award, Languages, DollarSign, MapPin } from 'lucide-react';

const ProfessionalProfile = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { API, language } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchProfile();
  }, [userId]);

  const fetchProfile = async () => {
    try {
      const [profileResponse, reviewsResponse] = await Promise.all([
        axios.get(`${API}/professionals/${userId}`),
        axios.get(`${API}/professionals/${userId}/reviews`),
      ]);
      setProfile(profileResponse.data);
      setReviews(reviewsResponse.data);
    } catch (error) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="professional-profile-loading">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    );
  }

  if (!profile) {
    return <div className="min-h-screen flex items-center justify-center" data-testid="professional-profile-not-found">Profile not found</div>;
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8" data-testid="professional-profile-page">
      <div className="max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {language === 'en' ? 'Back' : language === 'it' ? 'Indietro' : 'Zurück'}
        </Button>

        {/* Profile Header */}
        <Card className="swiss-shadow-lg mb-6">
          <CardContent className="pt-6">
            <div className="flex items-start space-x-6">
              <Avatar className="w-24 h-24" data-testid="professional-profile-avatar">
                {profile.profile_photo_url && <AvatarImage src={profile.profile_photo_url} alt={profile.display_name} />}
                <AvatarFallback className="text-2xl">{profile.display_name?.[0] || 'H'}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-2">
                  <h1 className="text-3xl font-bold font-heading" data-testid="professional-profile-name">{profile.display_name}</h1>
                  {profile.verified_badge && (
                    <Badge className="bg-blue-600" data-testid="professional-verified-badge" title="Helply reviewed submitted information and documents. This is not a safety certification or service-quality guarantee.">
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Helply badge
                    </Badge>
                  )}
                </div>
                
                {profile.ratings_avg > 0 && (
                  <div className="flex items-center space-x-2 mb-3" data-testid="professional-profile-rating">
                    <div className="flex items-center">
                      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      <span className="font-bold ml-1">{profile.ratings_avg.toFixed(1)}</span>
                    </div>
                    <span className="text-gray-500">({profile.total_reviews} reviews)</span>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-4">
                  {profile.hourly_rate && (
                    <div className="flex items-center space-x-2">
                      <DollarSign className="w-5 h-5 text-green-600" />
                      <div>
                        <p className="text-xs text-gray-500">Hourly Rate</p>
                        <p className="font-bold" data-testid="professional-profile-hourly-rate">CHF {profile.hourly_rate}</p>
                      </div>
                    </div>
                  )}
                  {profile.languages && profile.languages.length > 0 && (
                    <div className="flex items-center space-x-2">
                      <Languages className="w-5 h-5 text-blue-600" />
                      <div>
                        <p className="text-xs text-gray-500">Languages</p>
                        <p className="font-medium">{profile.languages.join(', ').toUpperCase()}</p>
                      </div>
                    </div>
                  )}
                  {profile.years_experience && (
                    <div className="flex items-center space-x-2">
                      <Award className="w-5 h-5 text-purple-600" />
                      <div>
                        <p className="text-xs text-gray-500">Experience</p>
                        <p className="font-medium">{profile.years_experience} years</p>
                      </div>
                    </div>
                  )}
                  {profile.work_location && profile.work_location.city && (
                    <div className="flex items-center space-x-2">
                      <MapPin className="w-5 h-5 text-red-600" />
                      <div>
                        <p className="text-xs text-gray-500">Location</p>
                        <p className="font-medium" data-testid="professional-profile-location">{profile.work_location.city}, {profile.work_location.canton}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {profile.bio && (
              <div className="mt-6">
                <h3 className="font-bold mb-2">About</h3>
                <p className="text-gray-700" data-testid="professional-profile-bio">{profile.bio}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Skills */}
        {profile.skills && profile.skills.length > 0 && (
          <Card className="swiss-shadow-lg mb-6">
            <CardHeader>
              <CardTitle className="font-heading">Skills</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {profile.skills.map((skill, idx) => (
                  <Badge key={idx} variant="outline" className="text-sm">
                    {skill}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Certifications */}
        {profile.certifications && profile.certifications.length > 0 && (
          <Card className="swiss-shadow-lg mb-6">
            <CardHeader>
              <CardTitle className="font-heading flex items-center">
                <Award className="w-5 h-5 mr-2 text-yellow-600" />
                Certifications
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {profile.certifications.map((cert, idx) => (
                  <li key={idx} className="flex items-center space-x-2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <span>{cert}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        )}

        {/* Reviews */}
        {reviews.length > 0 && (
          <Card className="swiss-shadow-lg">
            <CardHeader>
              <CardTitle className="font-heading">Reviews</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {reviews.map((review) => (
                  <div key={review.id} className="border-b pb-4 last:border-0" data-testid={`professional-review-${review.id}`}>
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="flex items-center space-x-1 mb-1">
                          {[...Array(5)].map((_, i) => (
                            <Star
                              key={i}
                              className={`w-4 h-4 ${
                                i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'
                              }`}
                            />
                          ))}
                        </div>
                        <p className="text-sm font-medium">Helply client</p>
                      </div>
                      <p className="text-xs text-gray-500" data-testid={`professional-review-date-${review.id}`}>
                        {new Date(review.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {review.review && <p className="text-gray-700 text-sm">{review.review}</p>}
                    {review.job_title && (
                      <p className="text-xs text-gray-500 mt-2">
                        Job: {review.job_title}
                      </p>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default ProfessionalProfile;